import { SEATS, trickWinner } from "@ravalmatic/bridge-engine/core";
import type { Card, PlayState, Seat } from "@ravalmatic/bridge-engine/core";
import { HandMode, ContractOutcome } from "../../Types";
import type { BoardPlaySnapshot } from "./BoardManager";
import { isLocalPlayerSeat } from "./PlayerSeats";

export type PlayerViewState = {
    readonly seat: Seat;
    readonly cards: readonly Card[];
    readonly handMode: HandMode;
    readonly legalCards: readonly Card[];
    readonly tricksWon: number;
    readonly previousTrickCard: Card | null;
    readonly contractLost: boolean;
    readonly turnActive: boolean;
    readonly thinking: boolean;
};

export type HUDViewState = {
    readonly completedTricks: number;
    readonly contractOutcome: "won" | "failed" | null;
};

export type BoardPlayViewState = {
    readonly players: readonly PlayerViewState[];
    readonly centerTrick: readonly { seat: Seat; card: Card }[];
    readonly collectedTricks: number;
    readonly hud: HUDViewState;
};

function getContractOutcome(state: PlayState): "made" | "defeated" | null {

    const requiredTricks = state.contract.level + 6;
    if (state.declarerTricks >= requiredTricks) {
        return "made";
    }
    if (state.defenderTricks > 13 - requiredTricks) {
        return "defeated";
    }
    return null;
}

export function buildHUDViewState(
    state: PlayState | null, forcedOutcome?: ContractOutcome
): HUDViewState {

    const outcome = state ? forcedOutcome ?? getContractOutcome(state) : null;
    return {
        completedTricks: state?.completedTricks.length ?? 0,
        contractOutcome: outcome && state
            ? (outcome === "made") === (state.contract.declarer % 2 === 0) ? "won" : "failed"
            : null
    };
}

export function buildBoardPlayViewState(
    snapshot: BoardPlaySnapshot,
    preferences: { showLastHand: boolean }
): BoardPlayViewState | null {

    const state = snapshot.state;
    if (state === null) {
        return null;
    }
    const completedTricks = state.completedTricks.length;
    // The engine completes a trick before its cards have been collected.
    const collectedTricks = snapshot.trickCollection
        ? snapshot.trickCollection.trickNumber - 1 : completedTricks;
    const previousTrick = state.completedTricks[collectedTricks - 1];
    const centerTrick = state.currentTrick.length > 0 ? state.currentTrick
        : snapshot.trickCollection
            ? state.completedTricks[snapshot.trickCollection.trickNumber - 1] : [];
    const tricksWon = [0, 0, 0, 0];
    state.completedTricks.forEach(trick => {
        tricksWon[trickWinner(trick, state.contract.strain)]++;
    });
    const hud = buildHUDViewState(state, snapshot.contractResult?.outcome);
    const hasBotError = snapshot.botError !== null;
    const players = SEATS.map((seat): PlayerViewState => {
        const interactive = !hasBotError && snapshot.isPlayerTurn && state.handToPlay === seat;
        const localPlayer = isLocalPlayerSeat(seat);
        const faceUp = localPlayer || seat === state.dummy;
        const turnActive = !hasBotError && !state.isOver && snapshot.legalCards.length > 0 && state.handToPlay === seat;
        return {
            seat,
            cards: snapshot.hands[seat],
            handMode: interactive ? HandMode.INTERACTIVE
                : faceUp ? HandMode.VISIBLE_READONLY : HandMode.HIDDEN,
            legalCards: interactive ? snapshot.legalCards : [],
            tricksWon: tricksWon[seat],
            previousTrickCard: preferences.showLastHand
                ? previousTrick?.find(play => play.seat === seat)?.card ?? null : null,
            contractLost: !localPlayer && hud.contractOutcome !== null
                && (seat % 2 === 0) === (hud.contractOutcome === "failed"),
            turnActive,
            thinking: turnActive && !snapshot.isPlayerTurn && !localPlayer
        };
    });
    return {
        players,
        centerTrick,
        collectedTricks,
        hud
    };
}
